// SAINTCHA concierge widget. The markup is snippets/saintcha-concierge.liquid, which renders nothing unless the
// concierge is switched on in theme settings with an https endpoint. Answers come from the concierge Worker
// (POST {endpoint}/chat). This file never adds anything to the bag by itself: every add is a button the
// shopper presses, and it uses the same cumulative cap + /cart/add.js + window.SaintchaBag.added() flow as the
// rest of the theme. Sets are never added here; set suggestions open the set builder, which owns the slots.
(() => {
  const root = document.querySelector('[data-saintcha-concierge]');
  if (!root || root.dataset.ready) return;
  const endpointRaw = String(root.dataset.endpoint || '').trim();
  if (!/^https:\/\/[^\s/]+/i.test(endpointRaw)) return;
  root.dataset.ready = 'true';
  const endpoint = /\/chat\/?$/.test(endpointRaw) ? endpointRaw : `${endpointRaw.replace(/\/+$/, '')}/chat`;

  const $ = (selector) => root.querySelector(selector);
  const launcher = $('[data-sf-launch]');
  const panel = $('[data-sf-panel]');
  const main = $('[data-sf-main]');
  const log = $('[data-sf-log]');
  const think = $('[data-sf-think]');
  const live = $('[data-sf-live]');
  const form = $('[data-sf-form]');
  const input = $('[data-sf-input]');
  const send = $('[data-sf-send]');
  const reset = $('[data-sf-reset]');
  const rootPath = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const STORE_KEY = 'saintcha-concierge-v1';
  const VISITOR_KEY = 'saintcha-concierge-visitor';
  const MAX_TURNS = 12;
  const MAX_CHARS = 1500;
  const TIMEOUT_MS = 35000;
  const NAME = String(root.dataset.name || 'Hana').replace(/[^A-Za-z\u00C0-\u024F .'-]/g, '').trim().slice(0, 20) || 'Hana';
  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  // Split a reply into chat-sized bubbles (paragraphs), at most 3, like someone texting.
  const bubbles = (text) => {
    const parts = String(text || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
    if (parts.length <= 3) return parts.length ? parts : [String(text || '')];
    return [parts[0], parts[1], parts.slice(2).join('\n\n')];
  };

  // ---------------------------------------------------------------- storage (always try/catch)
  const readJson = (storage, key) => { try { const raw = window[storage].getItem(key); return raw ? JSON.parse(raw) : null; } catch (error) { return null; } };
  const writeJson = (storage, key, value) => { try { window[storage].setItem(key, JSON.stringify(value)); } catch (error) { /* private mode or full */ } };
  const randomId = () => {
    try { const bytes = new Uint8Array(12); window.crypto.getRandomValues(bytes); return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join(''); } catch (error) { return `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`; }
  };
  const visitorId = (() => {
    // Anonymous random id, only used by the Worker for fair rate limits. No personal data.
    let id = null;
    try { id = window.localStorage.getItem(VISITOR_KEY); } catch (error) { id = null; }
    if (!id || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) { id = randomId(); try { window.localStorage.setItem(VISITOR_KEY, id); } catch (error) { /* ignore */ } }
    return id;
  })();

  let limits = {};
  try { limits = JSON.parse(($('[data-sf-limits]') || {}).textContent || '{}') || {}; } catch (error) { limits = {}; }

  const saved = readJson('sessionStorage', STORE_KEY);
  const state = { entries: Array.isArray(saved && saved.entries) ? saved.entries.slice(-40) : [], busy: false };
  const persist = () => writeJson('sessionStorage', STORE_KEY, { entries: state.entries.slice(-40) });

  // ---------------------------------------------------------------- safe rendering
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  const unescapeHtml = (value) => String(value).replace(/&(amp|lt|gt|quot|#39);/g, (m, k) => ({ amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'" }[k]));
  const SHOP_HOSTS = ['saintcha.com', 'www.saintcha.com'];
  // Links are allowed to saintcha.com only: a site-relative path, or an absolute https saintcha.com URL.
  const safeHref = (raw) => {
    const url = String(raw || '').trim();
    if (/^\/(?!\/)/.test(url) && !/[\s"'<>\\]/.test(url)) return url;
    try {
      const parsed = new URL(url);
      if (parsed.protocol === 'https:' && SHOP_HOSTS.includes(parsed.hostname)) return parsed.href;
    } catch (error) { /* not a URL */ }
    return '';
  };
  const safePath = (raw, pattern) => (typeof raw === 'string' && pattern.test(raw) ? raw : '');
  const safeImage = (raw) => {
    try {
      const parsed = new URL(String(raw || ''));
      if (parsed.protocol !== 'https:' || !/(^|\.)cdn\.shopify\.com$|(^|\.)saintcha\.com$/.test(parsed.hostname)) return '';
      if (/cdn\.shopify\.com$/.test(parsed.hostname)) parsed.searchParams.set('width', '160');
      return parsed.href;
    } catch (error) { return ''; }
  };
  const inline = (escaped) => escaped
    .replace(/\[([^\]]{1,80})\]\(([^)\s]{1,300})\)/g, (match, label, href) => {
      const safe = safeHref(unescapeHtml(href));
      return safe ? `<a href="${escapeHtml(safe)}">${label}</a>` : label;
    })
    .replace(/\*\*([^*]{1,120})\*\*/g, '<strong>$1</strong>');
  // Markdown-light: paragraphs, "- " lists, **bold** and saintcha.com links. Everything else is escaped text.
  const formatReply = (text) => {
    const blocks = escapeHtml(text).replace(/\r/g, '').split(/\n{2,}/);
    return blocks.map((block) => {
      const lines = block.split('\n').filter((line) => line.trim());
      if (lines.length && lines.every((line) => /^\s*([-*•]|\d+[.)])\s+/.test(line))) {
        const ordered = /^\s*\d/.test(lines[0]);
        const items = lines.map((line) => `<li>${inline(line.replace(/^\s*([-*•]|\d+[.)])\s+/, ''))}</li>`).join('');
        return ordered ? `<ol>${items}</ol>` : `<ul>${items}</ul>`;
      }
      return `<p>${lines.map(inline).join('<br>')}</p>`;
    }).join('');
  };
  const plain = (text) => String(text || '').replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  const money = (value) => { const n = Number(value); if (!Number.isFinite(n)) return ''; return `$${n % 1 ? n.toFixed(2) : n.toFixed(0)}`; };
  const formatLabel = (format) => ({ tin: 'tin', bag: 'original bag', refill: 'SAINTCHA-packed refill' }[format] || '');

  const PRODUCT_PATH = /^\/products\/[a-z0-9-]+(\?variant=\d+)?$/;
  const SET_PATH = /^\/pages\/sets\?variants=\d+(,\d+){0,3}$/;
  const LINK_PATH = /^\/(pages|products|collections|cart|search)(\/[a-z0-9-]+)*\/?(\?[a-z0-9=,&%._-]*)?(#[a-z0-9-]+)?$/i;
  const withRoot = (path) => (rootPath() !== '/' && path.startsWith('/') ? rootPath().replace(/\/$/, '') + path : path);

  const cardHtml = (card) => {
    const id = String(card.variant_id || '').replace(/\D/g, '');
    if (!id) return '';
    const image = safeImage(card.image);
    const href = safePath(card.url, PRODUCT_PATH);
    const available = card.available === true && limits[id] !== 0;
    const meta = [card.variant_title, formatLabel(card.format)].filter(Boolean).join(' ');
    const perBowl = card.per_bowl ? `${money(card.per_bowl)} a bowl` : '';
    const name = escapeHtml(card.short_name || card.title || 'SAINTCHA');
    const compare = Number(card.compare_at) > Number(card.price) ? `<s>${money(card.compare_at)}</s>` : '';
    return `<article class="sf-card" data-sf-card>
      ${image ? `<img class="sf-card__img" src="${escapeHtml(image)}" alt="" width="58" height="70" loading="lazy">` : '<span class="sf-card__img--empty" aria-hidden="true"></span>'}
      <div>
        ${card.house ? `<span class="sf-card__house">${escapeHtml(card.house)}</span>` : ''}
        ${href ? `<a class="sf-card__name" href="${escapeHtml(withRoot(href))}">${name}</a>` : `<span class="sf-card__name">${name}</span>`}
        <span class="sf-card__meta">${escapeHtml([meta, perBowl].filter(Boolean).join(' · '))}${meta || perBowl ? ' · ' : ''}${available ? 'In stock' : '<span class="is-out">Sold out</span>'}</span>
      </div>
      <div class="sf-card__buy">
        <span class="sf-card__price">${compare}${money(card.price)}</span>
        <button type="button" class="sf-btn" data-sf-add="${id}" data-image="${escapeHtml(image)}" ${available ? '' : 'disabled'} aria-label="${available ? `Add ${name} ${escapeHtml(card.variant_title || '')} to bag` : `${name} is sold out`}">${available ? 'Add to bag' : 'Sold out'}</button>
      </div>
      <p class="sf-card__status" role="status" data-sf-status></p>
    </article>`;
  };

  const actionsHtml = (actions) => {
    let html = '';
    const buttons = [];
    (Array.isArray(actions) ? actions : []).forEach((action) => {
      if (!action || typeof action !== 'object') return;
      if (action.type === 'open_set_builder') {
        const href = safePath(action.url, SET_PATH);
        if (!href) return;
        const rows = (Array.isArray(action.items) ? action.items : []).slice(0, 4).map((item) => {
          const saving = Number(item.set_price) < Number(item.price);
          const label = [item.name, item.variant_title, formatLabel(item.format)].filter(Boolean).join(' ');
          return `<div class="sf-set__row"><span>${escapeHtml(label)}</span><span>${saving ? `<s>${money(item.price)}</s>` : ''}${money(saving ? item.set_price : item.price)}</span></div>`;
        }).join('');
        html += `<div class="sf-set"><span class="sf-set__title">Suggested set</span>${rows}
          ${Number(action.savings) > 0 ? `<div class="sf-set__row"><span>Tin savings</span><span>−${money(action.savings)}</span></div>` : ''}
          <div class="sf-set__row sf-set__total"><span>Set price${action.free_shipping ? ' · ships free' : ''}</span><span>${money(action.total)}</span></div>
          <a class="sf-btn" href="${escapeHtml(withRoot(href))}">${escapeHtml(action.label || 'Open in set builder')}</a></div>`;
      } else if (action.type === 'add') {
        const id = String(action.variant_id || '').replace(/\D/g, '');
        if (!id || limits[id] === 0) return;
        buttons.push(`<button type="button" class="sf-btn" data-sf-add="${id}" data-image="${escapeHtml(safeImage(action.image))}">${escapeHtml(action.label || 'Add to bag')}${action.price ? ` · ${money(action.price)}` : ''}</button>`);
      } else if (action.type === 'link') {
        const href = safePath(action.url, LINK_PATH);
        if (href) buttons.push(`<a class="sf-btn sf-btn--ghost" href="${escapeHtml(withRoot(href))}">${escapeHtml(action.label || 'Open')}</a>`);
      }
    });
    if (buttons.length) html += `<div class="sf-actions">${buttons.join('')}<p class="sf-actions__status" role="status" data-sf-status></p></div>`;
    return html;
  };

  const entryNode = (entry, index, fresh) => {
    const li = document.createElement('li');
    li.className = `sf-msg ${entry.role === 'user' ? 'sf-me' : 'sf-ai'}${fresh ? ' is-new' : ''}`;
    if (entry.role === 'user') {
      li.innerHTML = `<span class="sf-visually-hidden">You: </span>${escapeHtml(entry.content)}`;
    } else if (entry.error) {
      li.innerHTML = `<div class="sf-error"><span class="sf-visually-hidden">Concierge: </span>${escapeHtml(entry.content)}${entry.retry ? '<br><button type="button" data-sf-retry>Try again</button>' : ''}</div>`;
    } else {
      const cards = (Array.isArray(entry.cards) ? entry.cards : []).slice(0, 3).map(cardHtml).join('');
      li.innerHTML = `<span class="sf-visually-hidden">Concierge: </span><div class="sf-text">${formatReply(entry.content)}</div>${cards ? `<div class="sf-cards">${cards}</div>` : ''}${actionsHtml(entry.actions)}`;
    }
    li.dataset.index = String(index);
    return li;
  };

  const renderLog = () => {
    log.textContent = '';
    state.entries.forEach((entry, index) => log.appendChild(entryNode(entry, index, false)));
    root.classList.toggle('has-log', state.entries.length > 0);
    reset.hidden = state.entries.length === 0;
  };
  const appendEntry = (entry) => {
    state.entries.push(entry);
    persist();
    log.appendChild(entryNode(entry, state.entries.length - 1, true));
    root.classList.add('has-log');
    reset.hidden = false;
    scrollToEnd();
  };
  const scrollToEnd = () => { main.scrollTop = main.scrollHeight; };

  // ---------------------------------------------------------------- open / close, focus trap
  let lastFocus = null;
  const isOpen = () => !panel.hidden;
  const open = () => {
    if (isOpen()) return;
    lastFocus = document.activeElement;
    panel.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('sf-open');
    scrollToEnd();
    window.setTimeout(() => input.focus({ preventScroll: true }), 30);
  };
  const close = () => {
    if (!isOpen()) return;
    panel.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('sf-open');
    const back = lastFocus && document.contains(lastFocus) && lastFocus !== document.body ? lastFocus : launcher;
    back.focus({ preventScroll: true });
  };
  const focusables = () => [...panel.querySelectorAll('a[href],button:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
    .filter((node) => !node.hidden && node.getClientRects().length > 0);
  document.addEventListener('keydown', (event) => {
    if (!isOpen()) return;
    // The bag drawer (opened after an add) handles its own keys while it is on top.
    if (document.documentElement.classList.contains('bag-drawer-open')) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    const nodes = focusables();
    if (!nodes.length) return;
    const first = nodes[0]; const last = nodes[nodes.length - 1];
    if (!panel.contains(document.activeElement)) { event.preventDefault(); first.focus(); return; }
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  // ---------------------------------------------------------------- talking to the Worker
  const readCart = async () => {
    try {
      const bag = window.SaintchaBag;
      const cart = bag && typeof bag.readCart === 'function' ? await bag.readCart()
        : await (await fetch(`${rootPath()}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } })).json();
      // Only ids, quantities and the total are sent: no line properties, no customer data.
      return { items: (cart.items || []).slice(0, 30).map((item) => ({ variant_id: item.variant_id, quantity: item.quantity })), total_price: cart.total_price };
    } catch (error) { return null; }
  };
  const pageContext = () => {
    const path = window.location.pathname.slice(0, 200);
    const match = path.match(/\/products\/([a-z0-9-]+)/);
    return { path, product_handle: match ? match[1] : '' };
  };
  const history = () => state.entries
    .filter((entry) => !entry.error && (entry.role === 'user' || entry.role === 'assistant'))
    .slice(-MAX_TURNS)
    .map((entry) => ({ role: entry.role, content: String(entry.content || '').slice(0, MAX_CHARS) }));

  const setBusy = (busy) => {
    state.busy = busy;
    think.hidden = !busy;
    send.disabled = busy;
    main.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) scrollToEnd();
  };

  const ask = async () => {
    if (state.busy) return;
    setBusy(true);
    const started = Date.now();
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), TIMEOUT_MS) : null;
    let entry;
    try {
      const body = { messages: history(), cart: await readCart(), page: pageContext(), visitor_id: visitorId, assistant_name: NAME };
      const response = await fetch(endpoint, {
        method: 'POST', mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller ? controller.signal : undefined,
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && typeof data.reply === 'string') {
        entry = { role: 'assistant', content: data.reply, cards: Array.isArray(data.cards) ? data.cards.slice(0, 3) : [], actions: Array.isArray(data.actions) ? data.actions.slice(0, 3) : [] };
      } else {
        const retry = response.status !== 413 && response.status !== 403;
        entry = { role: 'assistant', error: true, retry: retry && response.status !== 429, content: typeof data.reply === 'string' && data.reply ? data.reply : 'The concierge is unavailable for a moment. Please try again.' };
      }
    } catch (error) {
      entry = { role: 'assistant', error: true, retry: true, content: error && error.name === 'AbortError' ? 'That took too long. Please try again.' : 'The concierge could not be reached. Check your connection and try again.' };
    } finally {
      if (timer) window.clearTimeout(timer);
    }
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!entry.error) {
      const parts = bubbles(entry.content);
      // Human pacing: a short typing beat, then one bubble per paragraph with a pause in between.
      const firstHold = reduced ? 0 : Math.max(0, Math.min(2200, 450 + parts[0].length * 14) - (Date.now() - started));
      if (firstHold) await wait(firstHold);
      for (let i = 0; i < parts.length; i += 1) {
        const last = i === parts.length - 1;
        if (i > 0 && !reduced) { scrollToEnd(); await wait(Math.min(1800, 500 + parts[i].length * 10)); }
        appendEntry(last ? { ...entry, content: parts[i] } : { role: 'assistant', content: parts[i], cards: [], actions: [] });
        if (!last) scrollToEnd();
      }
    } else {
      appendEntry(entry);
    }
    setBusy(false);
    live.textContent = '';
    window.setTimeout(() => { live.textContent = plain(entry.content); }, 50);
  };

  const submit = (text) => {
    const message = String(text || '').trim().slice(0, MAX_CHARS);
    if (!message || state.busy) return;
    appendEntry({ role: 'user', content: message });
    input.value = '';
    autosize();
    ask();
  };

  // ---------------------------------------------------------------- add to bag (same flow as the theme)
  const status = (button, text) => {
    const node = button.closest('[data-sf-card], .sf-actions');
    const target = node && node.querySelector('[data-sf-status]');
    if (target) target.textContent = text || '';
  };
  const addToBag = async (button) => {
    const id = button.dataset.sfAdd;
    if (!id || button.disabled) return;
    const bag = window.SaintchaBag;
    const limit = Object.prototype.hasOwnProperty.call(limits, id) ? limits[id] : undefined;
    status(button, '');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      if (limit === 0) { status(button, 'Sold out'); return; }
      if (bag && typeof bag.capQuantity === 'function') {
        // Cumulative limit: the Liquid limit minus what is already in the bag.
        const cap = await bag.capQuantity(id, 1, limit);
        if (cap.allowed < 1) { status(button, bag.LIMIT_MESSAGE); if (typeof bag.showFull === 'function') bag.showFull(); return; }
      }
      const response = await fetch(`${rootPath()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });
      if (response.ok) {
        if (bag && typeof bag.added === 'function') await bag.added({ source: button, image: button.dataset.image || '' });
        else window.location.assign(`${rootPath()}cart`);
      } else {
        const error = await response.json().catch(() => ({}));
        status(button, typeof error.description === 'string' && error.description ? error.description : 'Could not add this item. Please try again.');
      }
    } catch (error) {
      status(button, 'Could not add this item. Please try again.');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  // ---------------------------------------------------------------- events
  const autosize = () => { input.style.height = 'auto'; input.style.height = `${Math.min(110, input.scrollHeight)}px`; };
  launcher.addEventListener('click', open);
  $('[data-sf-close]').addEventListener('click', close);
  reset.addEventListener('click', () => {
    if (state.busy) return;
    state.entries = [];
    persist();
    renderLog();
    input.focus();
  });
  form.addEventListener('submit', (event) => { event.preventDefault(); submit(input.value); });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) { event.preventDefault(); submit(input.value); }
  });
  input.addEventListener('input', autosize);
  panel.addEventListener('click', (event) => {
    const prompt = event.target.closest('[data-sf-prompt]');
    if (prompt) { submit(prompt.dataset.sfPrompt); return; }
    const add = event.target.closest('[data-sf-add]');
    if (add) { addToBag(add); return; }
    const retry = event.target.closest('[data-sf-retry]');
    if (retry && !state.busy) {
      const last = state.entries[state.entries.length - 1];
      if (last && last.error) { state.entries.pop(); persist(); renderLog(); }
      ask();
    }
  });

  // ---------------------------------------------------------------- stay clear of bottom bars
  // The PDP sticky buy bar and the phone set-builder bar are pinned to the bottom of the screen. When one is
  // there, the launcher is lifted by its height so it never covers "Add to bag".
  const BARS = ['[data-stickybuy]', '.saintcha-set__summary'];
  let frame = 0;
  const lift = () => {
    frame = 0;
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    let amount = 0;
    BARS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((bar) => {
        const style = window.getComputedStyle(bar);
        if (style.display === 'none' || style.visibility === 'hidden') return;
        if (!/sticky|fixed/.test(style.position) || style.bottom !== '0px') return;
        const rect = bar.getBoundingClientRect();
        if (rect.height && rect.top < viewport && rect.bottom >= viewport - 2) amount = Math.max(amount, Math.min(240, viewport - rect.top));
      });
    });
    root.style.setProperty('--sf-lift', `${Math.round(amount)}px`);
  };
  const queueLift = () => { if (!frame) frame = window.requestAnimationFrame ? window.requestAnimationFrame(lift) : window.setTimeout(lift, 16); };
  window.addEventListener('scroll', queueLift, { passive: true });
  window.addEventListener('resize', queueLift);

  renderLog();
  launcher.hidden = false;
  lift();
  window.SaintchaConcierge = { open, close, send: submit };
})();
